import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  DeleteMeResult,
  DeleteMeOptions,
  PatchMeResult,
  PatchMeOptions,
  GetMeOptions,
  GetMeResult,
  getMe,
  DeleteMeFields,
  PatchMeFields,
  GetMeFields,
  deleteMe,
  patchMe,
} from '../fetchers/me';
import { useCallback } from 'react';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetMeOptions<Fields extends GetMeFields> = GetMeOptions<Fields>;

/**
 * Get the user
 * This method returns the authenticated user.
 */

export function useGetMe<Fields extends GetMeFields>(
  getOptions: UseGetMeOptions<Fields> | (() => UseGetMeOptions<Fields> | null),
  config?: SWRConfiguration<GetMeResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options ? `/me${serializeQuery(options)}` : () => null,
    options
      ? () => {
          return getMe({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetMe, {
    endpoint: '/me',
    method: 'GET',
  });
}

/**
 * Get the user
 * This method returns the authenticated user.
 */

export function useGetMeLazy<Fields extends GetMeFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<GetMeResult<Fields> | undefined>();

  let callback = useCallback(
    async (options: UseGetMeOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/me${serializeQuery(options)}`,
          getMe({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetMeLazy, {
    endpoint: '/me',
    method: 'GET',
  });
}

type UseDeleteMeOptions<Fields extends DeleteMeFields> = DeleteMeOptions<Fields>;

/**
 * Delete the user's account
 * This method deletes the Vimeo account of the authenticated user.
 */

export function useDeleteMe<Fields extends DeleteMeFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<DeleteMeResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UseDeleteMeOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/me${serializeQuery(options)}`,
          deleteMe({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useDeleteMe, {
    endpoint: '/me',
    method: 'DELETE',
  });
}

type UsePatchMeOptions<Fields extends PatchMeFields> = PatchMeOptions<Fields>;

/**
 * Edit the user's account
 * This method edits the Vimeo account of the authenticated user.
 */

export function usePatchMe<Fields extends PatchMeFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<PatchMeResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UsePatchMeOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/me${serializeQuery(options)}`,
          patchMe({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePatchMe, {
    endpoint: '/me',
    method: 'PATCH',
  });
}
